import React, { useCallback, useState } from "react";
import { Button, Input, Modal, Select, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { SorterPlanSummary } from "../../../types/sortingMachine";
import useLang from "../../../hooks/useLang";

const PRIMARY_BUTTON_STYLE = {
	backgroundColor: "#4F16C2",
	borderColor: "#4F16C2",
	color: "#ffffff",
} as const;

interface PlanManagerProps {
	plans: SorterPlanSummary[];
	selectedPlanId: number | null;
	onSelectPlan: (planId: number) => void;
	onCreatePlan: (name: string) => void;
	onRenamePlan: (planId: number, name: string) => void;
	onDeletePlan: (planId: number) => void;
}

const PlanManager = ({
	plans,
	selectedPlanId,
	onSelectPlan,
	onCreatePlan,
	onRenamePlan,
	onDeletePlan,
}: PlanManagerProps) => {
	const { strings } = useLang("sortingMachineTab");

	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [createName, setCreateName] = useState("");

	const [renameModalOpen, setRenameModalOpen] = useState(false);
	const [renameName, setRenameName] = useState("");

	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;

	const planOptions = plans.map((p) => ({
		value: p.id,
		label: p.name,
	}));

	const handleSelectChange = useCallback(
		(value: number) => {
			onSelectPlan(value);
		},
		[onSelectPlan]
	);

	const handleOpenCreate = useCallback(() => {
		setCreateName("");
		setCreateModalOpen(true);
	}, []);

	const handleCreateConfirm = useCallback(() => {
		const trimmed = createName.trim();
		if (!trimmed) return;
		onCreatePlan(trimmed);
		setCreateModalOpen(false);
		setCreateName("");
	}, [createName, onCreatePlan]);

	const handleOpenRename = useCallback(() => {
		if (!selectedPlan) return;
		setRenameName(selectedPlan.name);
		setRenameModalOpen(true);
	}, [selectedPlan]);

	const handleRenameConfirm = useCallback(() => {
		const trimmed = renameName.trim();
		if (!trimmed || !selectedPlan) return;
		onRenamePlan(selectedPlan.id, trimmed);
		setRenameModalOpen(false);
	}, [renameName, selectedPlan, onRenamePlan]);

	const handleOpenDelete = useCallback(() => {
		setDeleteConfirmOpen(true);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		if (!selectedPlan) return;
		onDeletePlan(selectedPlan.id);
		setDeleteConfirmOpen(false);
	}, [selectedPlan, onDeletePlan]);

	const handleCreateNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setCreateName(e.target.value);
		},
		[]
	);

	const handleRenameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setRenameName(e.target.value);
		},
		[]
	);

	return (
		<>
			<div className="flex items-center gap-3 flex-wrap">
				<Select
					value={selectedPlanId ?? undefined}
					onChange={handleSelectChange}
					options={planOptions}
					placeholder={
						strings?.planManager?.selectPlaceholder ??
						"Select a plan"
					}
					className="min-w-[220px]"
				/>

				<Button icon={<PlusOutlined />} onClick={handleOpenCreate}>
					{strings?.planManager?.newPlan ?? "New Plan"}
				</Button>

				<Button
					icon={<EditOutlined />}
					onClick={handleOpenRename}
					disabled={!selectedPlan}
				>
					{strings?.planManager?.rename ?? "Rename"}
				</Button>

				<Button
					icon={<DeleteOutlined />}
					danger
					onClick={handleOpenDelete}
					disabled={!selectedPlan || selectedPlan.isActive}
				>
					{strings?.planManager?.delete ?? "Delete"}
				</Button>

				{selectedPlan?.isActive ? (
					<Tag color="green">
						{strings?.planManager?.active ?? "Active"}
					</Tag>
				) : null}
			</div>

			<Modal
				title={strings?.planManager?.createTitle ?? "Create New Plan"}
				open={createModalOpen}
				onCancel={() => setCreateModalOpen(false)}
				onOk={handleCreateConfirm}
				okButtonProps={{
					disabled: createName.trim().length === 0,
					style: PRIMARY_BUTTON_STYLE,
				}}
				okText={strings?.planManager?.create ?? "Create"}
				cancelText={strings?.planManager?.cancel ?? "Cancel"}
			>
				<Input
					placeholder={
						strings?.planManager?.namePlaceholder ??
						"Enter plan name"
					}
					value={createName}
					onChange={handleCreateNameChange}
					onPressEnter={handleCreateConfirm}
					autoFocus
				/>
			</Modal>

			<Modal
				title={strings?.planManager?.renameTitle ?? "Rename Plan"}
				open={renameModalOpen}
				onCancel={() => setRenameModalOpen(false)}
				onOk={handleRenameConfirm}
				okButtonProps={{
					disabled: renameName.trim().length === 0,
					style: PRIMARY_BUTTON_STYLE,
				}}
				okText={strings?.planManager?.save ?? "Save"}
				cancelText={strings?.planManager?.cancel ?? "Cancel"}
			>
				<Input
					value={renameName}
					onChange={handleRenameChange}
					onPressEnter={handleRenameConfirm}
					autoFocus
				/>
			</Modal>

			<Modal
				title={strings?.planManager?.deleteTitle ?? "Delete Plan"}
				open={deleteConfirmOpen}
				onCancel={() => setDeleteConfirmOpen(false)}
				onOk={handleDeleteConfirm}
				okText={strings?.planManager?.confirmDelete ?? "Yes, delete"}
				cancelText={strings?.planManager?.cancel ?? "Cancel"}
				okButtonProps={{ danger: true }}
			>
				<p>
					{strings?.planManager?.deleteMessage ??
						"Are you sure you want to delete the plan"}{" "}
					<strong>{selectedPlan?.name}</strong>?
				</p>
			</Modal>
		</>
	);
};

export default PlanManager;
